import { ButtonSwitcher } from "@lux/atoms/components/ButtonSwitcher";
import { LuxLink } from "@lux/atoms/components/LuxLink";
import { GlowTable } from "@components/GlowTable";
import { useZmClient } from "@components/hooks/useZmClient";
import { SolAmount } from "@glow/react/components/SolAmount";
import {
  formatNumber,
  getGlowImageUrl,
  NftCollection,
  pluralize,
} from "@luma-team/shared";
import { SquareNftImage } from "@glow/react/components/nfts/SquareNftImage";
import { ResponsiveBreakpoint } from "@utils/StyleConstants";
import { useState } from "react";

export const Trending = () => {
  const [period, setPeriod] = useState<"1d" | "7d" | "30d">("7d");
  const { data } = useZmClient({
    path: "/solana/explore/get-nft-collections",
    args: {
      sort_column: `volume_${period}_sol`,
      sort_direction: "desc",
      pagination_limit: 10,
    },
  });

  return (
    <div className="trending zm-container">
      <div className={"flex-center spread mb-4"}>
        <h2 className={"mb-0"}>Trending Collections</h2>

        <div className="desktop-only">
          <ButtonSwitcher
            options={[
              { value: "1d", label: "1D" },
              { value: "7d", label: "7D" },
              { value: "30d", label: "30D" },
            ]}
            value={period}
            setValue={(value) => setPeriod(value)}
            smallSizeBreakpoint={"always"}
          />
        </div>
      </div>

      <div className="desktop-only">
        <CollectionsTable
          collections={data?.entries}
          loading={data == null}
          period={period}
        />
      </div>
      <div className="mobile-only">
        <CollectionsTableMobile collections={data?.entries || []} />
      </div>

      <style jsx>{`
        .trending {
          padding: 0 var(--side-padding);
        }

        .desktop-only {
          display: block;
          @media (max-width: ${ResponsiveBreakpoint.small}) {
            display: none;
          }
        }

        .mobile-only {
          display: none;
          @media (max-width: ${ResponsiveBreakpoint.small}) {
            display: block;
          }
        }
      `}</style>
    </div>
  );
};

const CollectionsTable = ({
  period,
  ...props
}: { period: "1d" | "7d" | "30d" } & (
  | {
      collections?: NftCollection.BasicInfo[];
      loading?: false;
    }
  | { loading: true }
)) => {
  return (
    <GlowTable<NftCollection.BasicInfo>
      shimmerHeight={40}
      loadingRows={10}
      getHref={(row) => `/collection/${row.magic_eden_symbol}`}
      columns={[
        {
          headerName: "Collection",
          render: ({ name, image_url, num_nfts }) => (
            <div className={"flex-center"}>
              <SquareNftImage
                imageUrl={image_url}
                name={name}
                size={40}
                className="my-1 mr-2"
                rounded
              />

              <div>
                <div className="fw-medium">{name}</div>
                <div className="text-tertiary fs-sm">
                  {formatNumber({ num: num_nfts })} {pluralize("NFT", num_nfts)}
                </div>
              </div>
            </div>
          ),
        },
        {
          headerName: `${period.toUpperCase()} Volume`,
          accessor: `volume_${period}_sol`,
          alignRight: true,
          render: (row) => (
            <div className="mono-number">
              <SolAmount sol={row[`volume_${period}_sol`]!} />
            </div>
          ),
        },
      ]}
      rows={"collections" in props ? props.collections : []}
      isLoading={"loading" in props && props.loading}
    />
  );
};

const CollectionsTableMobile = ({
  collections,
}: {
  collections: NftCollection.BasicInfo[];
}) => {
  return (
    <div className="collection-mobile-table">
      {collections?.map(
        ({ api_id, image_url, name, magic_eden_symbol, num_nfts }) => (
          <LuxLink
            href={`/collection/${magic_eden_symbol}`}
            key={api_id}
            className={"block collection-card rounded"}
          >
            <div className="flex-center spread">
              <div className="flex-center">
                {image_url ? (
                  <img
                    src={
                      getGlowImageUrl({
                        url: image_url,
                        height: 40,
                        width: 40,
                      })!
                    }
                    className={"rounded mr-3"}
                    style={{ width: 40, height: 40 }}
                  />
                ) : (
                  <div
                    style={{ width: 40, height: 40 }}
                    className={"rounded mr-3"}
                  />
                )}

                <div>
                  <div className="fw-medium">{name}</div>

                  <div className="text-tertiary fs-sm">
                    {formatNumber({ num: num_nfts })}{" "}
                    {pluralize("NFT", num_nfts)}
                  </div>
                </div>
              </div>
            </div>
          </LuxLink>
        )
      )}

      <style jsx>{`
        .collection-mobile-table {
          margin-top: -0.5rem;

          :global(a) {
            color: inherit;
          }

          :global(.collection-card) {
            margin: -0.25rem -1rem;
            padding: 0.75rem 1rem;

            &:hover {
              background-color: var(--hover-bg-color);
            }
          }
        }
      `}</style>
    </div>
  );
};
